/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2026
///                                    RXO Inc.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of RXO Inc. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

import Modal from "../../Modal";
import SystemErrorModal from "../../SystemErrorModal";
import Spinner from "../../icons/Spinner";
import messages from "../../../helpers/messages";
import {
  awardStatus,
  closeLostDecision,
  closeLostReasonCustomer,
  closeLostReasonRxo
} from "../../../helpers/types";
import { isEffectiveCoyoteBid } from "../../../helpers/isEffectiveCoyoteBid";

const CUSTOMER_REASON_OPTIONS = [
  {
    value: closeLostReasonCustomer.bidCanceledOrPostponed,
    message: messages.closeLostReasonCustomerBidCanceledPostponed
  },
  {
    value: closeLostReasonCustomer.capabilitySolutionTechnology,
    message: messages.closeLostReasonCustomerCapabilitySolutionTechnology
  },
  {
    value: closeLostReasonCustomer.contractualTerms,
    message: messages.closeLostReasonCustomerContractualTerms
  },
  {
    value: closeLostReasonCustomer.noRelationship,
    message: messages.closeLostReasonCustomerNoRelationship
  },
  { value: closeLostReasonCustomer.price, message: messages.closeLostReasonCustomerPrice },
  {
    value: closeLostReasonCustomer.marketStudy,
    message: messages.closeLostReasonCustomerMarketStudy
  },
  {
    value: closeLostReasonCustomer.riskOfChange,
    message: messages.closeLostReasonCustomerRiskOfChange
  },
  {
    value: closeLostReasonCustomer.timeConstraintsMissedDeadline,
    message: messages.closeLostReasonCustomerTimeConstraintsMissedDeadline
  }
];

const RXO_REASON_OPTIONS = [
  {
    value: closeLostReasonRxo.capabilitySolutionTechnology,
    message: messages.closeLostReasonRxoCapabilitySolutionTechnology
  },
  { value: closeLostReasonRxo.capacity, message: messages.closeLostReasonRxoCapacity },
  {
    value: closeLostReasonRxo.contractualTermsFinancialRisk,
    message: messages.closeLostReasonRxoContractualTermsFinancialRisk
  },
  {
    value: closeLostReasonRxo.internalResources,
    message: messages.closeLostReasonRxoInternalResources
  },
  { value: closeLostReasonRxo.relationship, message: messages.closeLostReasonRxoRelationship },
  {
    value: closeLostReasonRxo.timeConstraintsMissedDeadline,
    message: messages.closeLostReasonRxoTimeConstraintsMissedDeadline
  }
];

function CloseBidModal({
  handleClose,
  handleCloseBidClick,
  handleCloseWonClick,
  isLoading,
  isFailure,
  summary,
  enableCloseLostCrmForm = true,
  forceCrmCloseLostForm = false
}) {
  const { enableCloseBidWon, selfPricedCloseLost, selfPricedCloseWon } = useSelector(
    state => state.featureFlags
  );
  const effectiveCoyoteBid = isEffectiveCoyoteBid(summary);
  const isSelfPriced = summary?.modes?.some(
    m => m.bidOwnerRepType === 1 && m.mode === 1
  );
  const hasWonStatus = summary?.modes?.some(
    x => x.awardStatus === awardStatus.won
  );
  const showCloseWonButton =
    enableCloseBidWon &&
    !effectiveCoyoteBid &&
    (!isSelfPriced || selfPricedCloseWon) &&
    hasWonStatus &&
    handleCloseWonClick;

  const useCrmCloseForm = Boolean(
    enableCloseLostCrmForm &&
      selfPricedCloseLost &&
      (isSelfPriced || forceCrmCloseLostForm)
  );

  const [decision, setDecision] = useState(closeLostDecision.customerDecision);
  const [reason, setReason] = useState(closeLostReasonCustomer.bidCanceledOrPostponed);
  const [winLossComments, setWinLossComments] = useState("");
  const [crmFormError, setCrmFormError] = useState("");
  const lastCrmPayloadRef = useRef(null);

  useEffect(() => {
    lastCrmPayloadRef.current = null;
    setCrmFormError("");
    setDecision(closeLostDecision.customerDecision);
    setReason(closeLostReasonCustomer.bidCanceledOrPostponed);
    setWinLossComments("");
  }, [useCrmCloseForm, summary?.bidId]);

  const onDecisionChange = nextDecision => {
    setDecision(nextDecision);
    setReason(
      nextDecision === closeLostDecision.customerDecision
        ? closeLostReasonCustomer.bidCanceledOrPostponed
        : closeLostReasonRxo.capabilitySolutionTechnology
    );
    setCrmFormError("");
  };

  const buildCrmPayload = () => {
    const trimmed = winLossComments.trim();
    if (decision === closeLostDecision.customerDecision) {
      return {
        decision: closeLostDecision.customerDecision,
        customerReason: reason,
        winLossComments: trimmed
      };
    }
    return {
      decision: closeLostDecision.rxoDecision,
      rxoReason: reason,
      winLossComments: trimmed
    };
  };

  const submitCrmClose = () => {
    const trimmed = winLossComments.trim();
    if (!trimmed) {
      setCrmFormError(messages.closeLostCrmValidationRequired.defaultMessage);
      return;
    }
    const body = buildCrmPayload();
    lastCrmPayloadRef.current = body;
    setCrmFormError("");
    handleCloseBidClick(body);
  };

  const getBodyMessage = () => {
    if (!enableCloseBidWon) {
      return messages.closeBidModalBodyMessageCoyote.defaultMessage;
    }
    if (effectiveCoyoteBid || (isSelfPriced && !selfPricedCloseWon)) {
      return messages.closeBidModalBodyMessageCoyote.defaultMessage;
    }
    return showCloseWonButton
      ? messages.closeBidModalBodyMessageWithCloseWon.defaultMessage
      : messages.closeBidModalBodyMessageWithoutCloseWon.defaultMessage;
  };

  const getPrimaryButton = () => {
    if (isLoading) {
      return (
        <button
          type="button"
          className="primary-button fr univers-67-bold-cond mt-12 mb-24 short-button"
          data-testid="spinner-button"
        >
          <Spinner
            className="spinner"
            fill="#ffffff"
            height="16px"
            width="16px"
          />
        </button>
      );
    }

    if (showCloseWonButton) {
      return (
        <button
          type="button"
          className="primary-button fr univers-67-bold-cond mt-12 mb-24 short-button"
          data-testid="closed-won"
          onClick={handleCloseWonClick}
        >
          {messages.closeWon.defaultMessage}
        </button>
      );
    }

    if (!enableCloseBidWon || effectiveCoyoteBid || (isSelfPriced && !selfPricedCloseWon)) {
      return (
        <button
          type="button"
          className="primary-button fr univers-67-bold-cond mt-12 mb-24 short-button"
          data-testid="close-bid"
          onClick={() => handleCloseBidClick()}
        >
          {messages.closeBid.defaultMessage}
        </button>
      );
    }

    return null;
  };

  const getTryAgainHandler = () => {
    if (useCrmCloseForm && lastCrmPayloadRef.current) {
      return () => handleCloseBidClick(lastCrmPayloadRef.current);
    }
    return showCloseWonButton ? handleCloseWonClick : () => handleCloseBidClick();
  };

  const reasonOptions =
    decision === closeLostDecision.customerDecision
      ? CUSTOMER_REASON_OPTIONS
      : RXO_REASON_OPTIONS;

  return (
    <>
      {!isFailure && (
        <Modal
          title={messages.closeBid.defaultMessage}
          open={true}
          handleClose={() => handleClose(false)}
          titleClass="modal__title_light uppercase"
          closeButtonClass="modal__close-button mr-16 mt-16"
          bodyClass="border-top-grey pl-24 pr-24 pt-16 pb-32"
          large={true}
          modalStyle={{ minHeight: useCrmCloseForm ? 320 : 196 }}
        >
          {useCrmCloseForm ? (
            <div className="font-size-16">
              <label className="block univers-67-cond mb-8" htmlFor="close-lost-decision">
                <FormattedMessage {...messages.closeLostDecisionLabel} />
              </label>
              <select
                id="close-lost-decision"
                className="one-whole mb-16 p-8 font-size-14 select--auto-height"
                value={decision}
                onChange={e => onDecisionChange(Number(e.target.value))}
                disabled={isLoading}
              >
                <option value={closeLostDecision.customerDecision}>
                  {messages.closeLostDecisionCustomer.defaultMessage}
                </option>
                <option value={closeLostDecision.rxoDecision}>
                  {messages.closeLostDecisionRxo.defaultMessage}
                </option>
              </select>

              <label className="block univers-67-cond mb-8" htmlFor="close-lost-reason">
                <FormattedMessage {...messages.closeLostReasonLabel} />
              </label>
              <select
                id="close-lost-reason"
                className="one-whole mb-16 p-8 font-size-14 select--auto-height"
                value={reason}
                onChange={e => setReason(Number(e.target.value))}
                disabled={isLoading}
              >
                {reasonOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.message.defaultMessage}
                  </option>
                ))}
              </select>

              <label className="block univers-67-cond mb-8" htmlFor="win-loss-comments">
                <FormattedMessage {...messages.lossCommentsLabel} />
              </label>
              <textarea
                id="win-loss-comments"
                className="one-whole mb-8 p-8 font-size-14"
                rows={5}
                maxLength={32768}
                style={{ maxHeight: 160, overflowY: "auto" }}
                value={winLossComments}
                onChange={e => setWinLossComments(e.target.value)}
                disabled={isLoading}
              />

              {crmFormError ? (
                <p className="text-rfq-red font-size-14 mb-8">{crmFormError}</p>
              ) : null}

              <div>
                {isLoading ? (
                  <button
                    type="button"
                    className="primary-button fr univers-67-bold-cond mt-12 mb-24 short-button"
                    data-testid="spinner-button"
                  >
                    <Spinner
                      className="spinner"
                      fill="#ffffff"
                      height="16px"
                      width="16px"
                    />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="primary-button fr univers-67-bold-cond mt-12 mb-24 short-button"
                    data-testid="submit-close-lost-crm"
                    onClick={submitCrmClose}
                  >
                    <FormattedMessage {...messages.closeLostCrmSubmit} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="secondary-button fr univers-67-bold-cond mt-12 mb-24 mr-12 px-16 short-button"
                  data-testid="cancel"
                >
                  {messages.cancel.defaultMessage}
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="font-size-16">{getBodyMessage()}</p>
              <div>
                {getPrimaryButton()}
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="secondary-button fr univers-67-bold-cond mt-12 mb-24 mr-12 px-16 short-button"
                  data-testid="cancel"
                >
                  {messages.cancel.defaultMessage}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
      {isFailure && (
        <SystemErrorModal
          open={isFailure}
          large={true}
          closeModal={() => handleClose(true)}
          handleExit={() => handleClose(true)}
          handleTryAgain={getTryAgainHandler()}
        />
      )}
    </>
  );
}

export default CloseBidModal;
